import { CSSResultGroup, LitElement, TemplateResult } from "lit";
import { holidaysCalendarStyles } from "./holidaysCalendar.css";
import { holidaysCalendarTemplate } from "./holidaysCalendar.html";
import { Calendar } from "vanilla-calendar-pro";
import { parseDates } from "vanilla-calendar-pro/utils";
import { customElement, query, state } from "lit/decorators.js";
import { UiFloatPopover } from "../../../common/popover";
import { HolidayExceptionDate } from "@lib/types";
import moment from "moment";
import { providePrimaryButtonsStyles } from "../../utils/providePrimaryButtonStyles";

type PopupTemplateOptions = Readonly<{
    isWorkdayButtonDisabled: boolean;
    isHolidayButtonDisabled: boolean;
}>;

/**
 * Gets template for actions popup.
 * @param Options Options.
 * @returns HTML template.
 */
function getActionsPopupTemplate({ isWorkdayButtonDisabled, isHolidayButtonDisabled }: PopupTemplateOptions): string {
    return `
    <style>
      .popup-items li.disabled {
        background-color: #BBB8BB;
        cursor: default;
      }
    </style>
    <div style="background: #fff;">
      <ul class="popup-items">
        <li id="workdays" class=${isWorkdayButtonDisabled ?
            "disabled" :
            ""}>Рабочий день</li>
        <li id="holidays" class=${isHolidayButtonDisabled ?
            "disabled" :
            ""}>Нерабочий день</li>
      </ul>
    </div>
  `;
}

const DEFAULT_WEEKDAY_NUMBERS = ["0", "6"];

export type HolidaysCalendarConfirmEvent = CustomEvent<HolidayExceptionDate[]>;

/** Holiday calendar event name. */
export enum HolidayCalendarEventName {
    Confirm = "holidaysCalendarConfirm",
    Cancel = "holidaysCalendarCancel",
}

/** Schedule holidays calendar. */
@customElement("holidays-calendar")
export class HolidaysCalendar extends LitElement {

    /** @inheritdoc */
    public static readonly styles: CSSResultGroup = holidaysCalendarStyles;

    private calendar: Calendar;

    @query(".calendar, .vc")
    private accessor calendarRoot: HTMLDivElement;

    @query(".submit-button")
    private accessor submitButton: HTMLElement;

    /** @inheritdoc */
    public override firstUpdated(): void {
        this.initCalendar();
        this.getExceptionDates();
        providePrimaryButtonsStyles([this.submitButton]);
    }

    /** @inheritdoc */
    public render(): TemplateResult {
        return holidaysCalendarTemplate.call(this);
    }

    @state()
    protected accessor currentYear: number = moment().get("years");

    private readonly exceptionWorkdays = new Set<string>();

    private readonly exceptionHolidays = new Set<string>();

    private readonly selectedDates = new Set<string>();

    private lastSelectedDate: string | null = null;

    /** Initial holidays exception dates. */
    public set holidayExceptionDates(value: readonly HolidayExceptionDate[]) {
        this.exceptionWorkdays.clear();
        this.exceptionHolidays.clear();

        value.forEach(exception => {
            if (exception.isHoliday) {
                this.exceptionHolidays.add(exception.date);
            } else {
                this.exceptionWorkdays.add(exception.date);
            }
        });

        if (this.calendar) {
            this.updateCalendarHolidays();
        }
    }

    private initCalendar(): void {
        try {
            this.calendar = new Calendar(
                this.calendarRoot,
                {
                    selectedTheme: "light",
                    type: "multiple",
                    monthsToSwitch: 1,
                    displayMonthsCount: 3,
                    selectionDatesMode: "multiple",
                    selectionMonthsMode: "only-arrows",
                    selectionYearsMode: "only-arrows",
                    enableEdgeDatesOnly: false,
                    selectedWeekends: [],
                    enableMonthChangeOnDayClick: false,
                    styles: {
                        month: "vc-month full-opacity",
                        year: "vc-year full-opacity",
                    },
                    locale: {
                        months: {
                            long: [
                                "Январь",
                                "Февраль",
                                "Март",
                                "Апрель",
                                "Май",
                                "Июнь",
                                "Июль",
                                "Август",
                                "Сентябрь",
                                "Октябрь",
                                "Ноябрь",
                                "Декабрь",
                            ],
                            short: [
                                "Январь",
                                "Февраль",
                                "Март",
                                "Апрель",
                                "Май",
                                "Июнь",
                                "Июль",
                                "Август",
                                "Сентябрь",
                                "Октябрь",
                                "Ноябрь",
                                "Декабрь",
                            ],
                        },
                        weekdays: {
                            long: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
                            short: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
                        },
                    },
                    onClickDate: (self, event) => {
                        const element = (event.target as HTMLElement).closest(".vc-date");
                        if (element) {
                            const date = element.getAttribute("data-vc-date");
                            if (date === null) {
                                return;
                            }
                            if (this.selectedDates.has(date)) {
                                this.selectedDates.delete(date);
                                this.lastSelectedDate = null;
                            } else {
                                const isShiftClick = event.shiftKey;
                                if (isShiftClick && this.lastSelectedDate !== null) {
                                    const dates = parseDates([`${this.lastSelectedDate}:${date}`, `${date}:${this.lastSelectedDate}`]);
                                    dates.forEach(selectedDate => this.selectedDates.add(selectedDate));
                                    this.lastSelectedDate = null;
                                    self.set({
                                        selectedDates: [...this.selectedDates],
                                    }, {
                                        year: false,
                                        month: false,
                                        locale: false,
                                        time: false,
                                    });
                                } else {
                                    this.selectedDates.add(date);
                                    this.lastSelectedDate = date;
                                }
                            }
                        }
                    },
                    onClickArrow: () => {
                        this.currentYear = this.calendar.context.selectedYear;
                        this.updateCalendarHolidays();
                    },
                },
            );
            this.calendar.init();
            this.updateCalendarHolidays();

            this.calendarRoot.addEventListener("contextmenu", event => {
                const date = (event.target as HTMLElement).closest(".vc-date")?.getAttribute("data-vc-date");
                if (date != null && !this.selectedDates.has(date)) {
                    return;
                }
                this.openActionsPopover(event);
            });
        } catch(e) {
            console.error("Error in <initCalendar> ", e);
        }
    }

    /** Gets exception dates for calendar.. */
    protected getExceptionDates(): void {
        this.updateCalendarHolidays();
    }

    /**
     * Opens actions popover.
     * @param event Click event.
     */
    protected openActionsPopover(event: PointerEvent): void {
        const isWorkdayButtonDisabled = [...this.selectedDates].every(date => !this.calendar.selectedHolidays.includes(date));
        const isHolidayButtonDisabled = [...this.selectedDates].every(date => this.calendar.selectedHolidays.includes(date));
        const popover = new UiFloatPopover(
            getActionsPopupTemplate({
                isWorkdayButtonDisabled,
                isHolidayButtonDisabled,
            }),
            {
                x: event.clientX,
                y: event.clientY,
            },
        );

        const workdaysButton = popover.root.querySelector("#workdays");
        const holidaysButton = popover.root.querySelector("#holidays");

        if (workdaysButton === null || holidaysButton === null) {
            return;
        }

        if (!isWorkdayButtonDisabled) {
            workdaysButton.addEventListener("click", () => {
                this.selectedDates.forEach(date => {
                    this.exceptionWorkdays.add(date);
                    this.exceptionHolidays.delete(date);
                });
                this.resetCalendarSelectedDates();
                this.updateCalendarHolidays();
                popover.destroy();
            });
        }

        if (!isHolidayButtonDisabled) {
            holidaysButton.addEventListener("click", () => {
                this.selectedDates.forEach(date => {
                    this.exceptionHolidays.add(date);
                    this.exceptionWorkdays.delete(date);
                });
                this.resetCalendarSelectedDates();
                this.updateCalendarHolidays();
                popover.destroy();
            });
        }
    }

    /** Resets calendar selected dates. */
    public resetCalendarSelectedDates(): void {
        this.calendar.set({
            selectedDates: [],
        }, {
            dates: true,
            year: false,
            month: false,
            time: false,
            locale: false,
        });
        this.selectedDates.clear();
    }

    private handleYearChange(mode: "increment" | "decrement"): void {
        const year = this.calendar.context.selectedYear;
        const newYear = mode === "increment" ?
            year + 1 :
            year - 1;
        this.currentYear = newYear;
        this.calendar.set({
            selectedYear: newYear,
            selectedMonth: 0,
        });
        this.updateCalendarHolidays();
    }

    /** Handles next year button click. */
    protected onNextYearButtonClick(): void {
        this.handleYearChange("increment");
    }

    /** Handles previous year button click. */
    protected onPrevYearButtonClick(): void {
        this.handleYearChange("decrement");
    }

    /** Updates calendar holidays. */
    protected updateCalendarHolidays(): void {
        const dateElements = this.calendarRoot.querySelectorAll(".vc-date");
        const holidayDates: string[] = [];
        dateElements.forEach(element => {
            const date = element.getAttribute("data-vc-date");
            const weekday = element.getAttribute("data-vc-date-week-day");
            if (date === null || weekday === null) {
                return;
            }
            const isDefaultWeekday = DEFAULT_WEEKDAY_NUMBERS.includes(weekday);
            if (isDefaultWeekday && !this.exceptionWorkdays.has(date) || this.exceptionHolidays.has(date)) {
                holidayDates.push(date);
            }
        });
        this.calendar.set({
            selectedHolidays: holidayDates,
        }, {
            dates: false,
            month: false,
            year: false,
            time: false,
            locale: false,
        });
    }

    /** Handles confirm button click. */
    protected handleConfirmButtonClick(): void {
        const dates = [
            ...Array.from(this.exceptionWorkdays)
                .map<HolidayExceptionDate>(date => ({
                    date,
                    isHoliday: false,
                })),
            ...Array.from(this.exceptionHolidays)
                .map<HolidayExceptionDate>(date => ({
                    date,
                    isHoliday: true,
                })),
        ];

        const event: HolidaysCalendarConfirmEvent = new CustomEvent(HolidayCalendarEventName.Confirm, {
            detail: dates,
        });

        this.dispatchEvent(event);
    }

    /** Handles cancel button click. */
    protected handleCancelButtonClick(): void {
        this.dispatchEvent(new CustomEvent(HolidayCalendarEventName.Cancel));
    }
}
